import { HttpErrorResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { OrdinaryObject } from "src/modules/utils/shared/ordinary-object.model";
import { Objects } from "src/modules/utils/shared/objects";
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from "src/modules/utils/shared/utils";
import { HttpConnectorError } from "../models/http-connector-error.model";
import { HttpRequestHistoryService } from "./http-request-history.service";

@Injectable({providedIn: "root"})
export class ErrorReportSerializerService {

    private readonly maximumResponseTextLength = 4000;
    private readonly maximumStackLength = 12000;
    private readonly maximumArrayLength = 50;
    private readonly maximumObjectPropertyCount = 100;
    private readonly maximumObjectDepth = 6;
    private readonly history = inject(HttpRequestHistoryService);

    serialize(error: unknown, backendErrorId?: string): string {
        try {
            return this.serializeInternal(error, backendErrorId);
        }
        catch (serializationError) {
            return Objects.toJson({
                name: error instanceof Error ? error.name : typeof error,
                message: error instanceof Error ? this.sanitizeAndCrop(error.message, this.maximumResponseTextLength) : "[Fehler konnte nicht serialisiert werden]",
                serializationError: serializationError instanceof Error ? serializationError.message : "Unbekannter Serialisierungsfehler"
            }, true);
        }
    }

    sanitizeText(value: string): string {
        if (value == null) {
            return null;
        }
        return value
            .replace(/(bearer\s+)[a-z0-9._~+/=-]+/gi, "$1[ENTFERNT]")
            .replace(/eyJ[a-z0-9_-]+\.[a-z0-9_-]+\.[a-z0-9_-]+/gi, "[JWT ENTFERNT]")
            .replace(/([?&](?:password|passwort|token|access_token|refresh_token|client_secret|secret|api[_-]?key)=)[^&#\s"']+/gi, "$1[ENTFERNT]")
            .replace(/("(?:password|passwort|token|accessToken|refreshToken|clientSecret|secret|authorization|cookie|apiKey)"\s*:\s*)"(?:\\.|[^"\\])*"/gi, "$1\"[ENTFERNT]\"");
    }

    private serializeInternal(error: unknown, backendErrorId?: string): string {
        let result: OrdinaryObject = {};
        if (error instanceof Error) {
            result.name = error.name;
            result.message = this.sanitizeAndCrop(error.message, this.maximumResponseTextLength);
            result.stack = this.sanitizeAndCrop(error.stack, this.maximumStackLength);
            if (error.cause != null) {
                result.cause = this.sanitizeValue(error.cause);
            }
        }
        else {
            result.value = this.sanitizeValue(error);
        }

        let response = error instanceof HttpConnectorError ? error.response : error instanceof HttpErrorResponse ? error : null;
        if (response != null) {
            let historyItem = this.history.getForError(response);
            let parsedUrl = historyItem == null ? this.history.parseUrl(response.url) : null;
            result.httpStatus = response.status;
            result.statusText = (response as unknown as {statusText: string}).statusText;
            result.method = error instanceof HttpConnectorError ? error.method.toUpperCase() : historyItem?.method;
            result.url = historyItem?.route ?? parsedUrl?.route;
            result.queryParameterNames = historyItem?.queryParameterNames ?? parsedUrl?.queryParameterNames;
            result.responseText = this.getResponseText(response.error);
            result.backendErrorId = backendErrorId ?? response.headers?.get("portal-backend-error-id");
            result.traceId = response.headers?.get("portal-trace-id") ?? response.headers?.get("traceparent") ?? response.headers?.get("x-request-id");
        }
        else if (!Utils.isNoe(backendErrorId)) {
            result.backendErrorId = backendErrorId;
        }

        return Objects.toJson(result, true);
    }

    private getResponseText(value: unknown): string {
        if (value == null) {
            return null;
        }
        if (typeof value == "string") {
            return Strings.crop(this.sanitizeText(value), this.maximumResponseTextLength);
        }
        if (value instanceof Blob) {
            return "[Binäre Antwort: " + value.type + ", " + value.size + " Bytes]";
        }
        if (value instanceof ArrayBuffer) {
            return "[Binäre Antwort: " + value.byteLength + " Bytes]";
        }
        if (value instanceof ProgressEvent) {
            return "[Netzwerkfehler: " + value.type + "]";
        }
        return Strings.crop(Objects.toJson(this.sanitizeValue(value), true), this.maximumResponseTextLength);
    }

    private sanitizeValue(value: unknown, depth = 0, ancestors = new WeakSet<object>()): unknown {
        if (value == null || typeof value == "number" || typeof value == "boolean") {
            return value;
        }
        if (typeof value == "string") {
            return this.sanitizeAndCrop(value, this.maximumResponseTextLength);
        }
        if (typeof value != "object") {
            return "[Wert vom Typ " + typeof value + "]";
        }
        if (value instanceof Date) {
            return value.toISOString();
        }
        if (value instanceof Error) {
            return {
                name: value.name,
                message: this.sanitizeAndCrop(value.message, this.maximumResponseTextLength),
                stack: this.sanitizeAndCrop(value.stack, this.maximumStackLength)
            };
        }
        if (depth >= this.maximumObjectDepth) {
            return "[Maximale Verschachtelungstiefe erreicht]";
        }
        if (ancestors.has(value)) {
            return "[Zyklische Referenz]";
        }
        ancestors.add(value);

        let result: unknown;
        if (Array.isArray(value)) {
            result = value.slice(0, this.maximumArrayLength).map(item => this.sanitizeValue(item, depth + 1, ancestors));
        }
        else {
            let sanitized: OrdinaryObject = {};
            for (let key of Object.keys(value).slice(0, this.maximumObjectPropertyCount)) {
                try {
                    sanitized[key] = this.isSensitiveProperty(key) ? "[ENTFERNT]" : this.sanitizeValue((value as OrdinaryObject)[key], depth + 1, ancestors);
                }
                catch {
                    sanitized[key] = "[Eigenschaft nicht lesbar]";
                }
            }
            result = sanitized;
        }
        ancestors.delete(value);
        return result;
    }

    private isSensitiveProperty(value: string): boolean {
        return /password|passwort|authorization|cookie|credential|clientsecret|api[_-]?key|(^|[_-])(access|refresh)?token($|[_-])|secret/i.test(value);
    }

    private sanitizeAndCrop(value: string, maximumLength: number): string {
        let sanitized = this.sanitizeText(value);
        return sanitized == null ? null : Strings.crop(sanitized, maximumLength);
    }
}
